import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import TripOriginIcon from "@mui/icons-material/TripOrigin";
import {Box, Button, List, ListItem, type SxProps} from "@mui/material";
import {type ChangeEvent, useEffect, useMemo} from "react";
import {useFormContext} from "react-hook-form";

type Props = {
    name: string;
    onChange?: (e: ChangeEvent<any>) => void;
    arrayName?: string;
    iteration?: number;
    options: {
        value: string;
        label: string;
        disabled?: boolean;
        hidden?: boolean;
        selected?: boolean;
    }[];
    sx?: SxProps;
};

const ButtonCollection = ({name, onChange, arrayName, iteration, options, sx}: Props) => {
    const fieldName =
        arrayName && iteration !== undefined ? `${arrayName}[${iteration}].${name}` : name;
    const formContext = useFormContext();

    const defaultSelectedOption = useMemo(() => {
        return options.filter(o => o.selected);
    }, [options]);

    useEffect(() => {
        const setDefaultValue = async () => {
            if (defaultSelectedOption.length) {
                formContext.setValue(fieldName, defaultSelectedOption[0].value);
            }
        };

        setDefaultValue().catch(console.error);
    }, [defaultSelectedOption, formContext.setValue, fieldName]);

    return (
        <Box
            sx={{
                ...sx,
                alignItems: "stretch",
                width: "100%",
                display: "flex",
                flexDirection: "row",
            }}
        >
            {options.map(option => {
                const selected =
                    option.selected || formContext.getValues(fieldName) === option.value;

                return (
                    !option.hidden && (
                        <Button
                            key={`toggle-option-${option.value}`}
                            value={option.value}
                            disabled={option.disabled}
                            onClick={async event => {
                                formContext.setValue(fieldName, option.value);
                                onChange?.(event);
                            }}
                            sx={{
                                borderRadius: "8px",
                                mr: 2,
                                mb: 1,
                                border: selected ? "1px solid #6b21a8" : "1px solid #e0e0e0",
                                p: "18px",
                                backgroundColor: selected ? "primary.50" : "#ffffff",
                                color: "#000",
                                flex: 1,
                            }}
                        >
                            {selected && (
                                <TripOriginIcon fontSize="small" sx={{mr: 1, color: "primary"}} />
                            )}
                            {!selected && (
                                <RadioButtonUncheckedIcon fontSize="small" sx={{mr: 1}} />
                            )}
                            {option.label}
                        </Button>
                    )
                );
            })}
            {formContext.formState.isSubmitted && !formContext.formState.isValid && (
                <Box
                    sx={{
                        color: "red",
                        ml: 2,
                        mt: 1,
                    }}
                >
                    {formContext.formState.errors[fieldName] && (
                        <List>
                            <ListItem key={`error-${fieldName}`}>
                                {formContext.formState.errors[fieldName]?.message?.toString()}
                            </ListItem>
                        </List>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default ButtonCollection;
